import React, { useState, useCallback, useEffect } from 'react';
import {
    Grid,
    Box,
    Typography,
    Avatar,
    Checkbox,
    Card,
    CardActions,
    CardMedia,
    Button,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BannerAdEchos from './echos/bannerAdEchos';
import { NoImage, Upload } from '../../../../assets';
import {
    getCampaignDetails,
    getAllCampaigns,
    uploadImage,
    deleteImage,
} from '../../../../redux/thunks/campaignThunks';
import {
    createMessage,
    updateMessage,
} from '../../../../redux/thunks/bannerAdCampaignThunk';
import { CustomButton, ConfirmationModal } from '../../../shared';

const useStyles = makeStyles((theme) => ({
    root: {
        '&$checked': {
            color: '#8C3F7D',
        },
    },
    checked: {},
}));

const createBannerAdSchema = yup
    .object({
        name: yup.string().required('Name is required'),
        bannerAd: yup.mixed().required('Please Upload an Image'),
        bannerAdUrl: yup.mixed(),
        isChecked: yup.boolean().default(false),
    })
    .required();


const updateBannerAdSchema = yup
    .object({
        name: yup.string().required('Name is required'),
        bannerAd: yup.mixed().notRequired(),
        bannerAdUrl: yup.mixed().notRequired(),
        isChecked: yup.boolean().default(false),
    })
    .required();

const getValidationSchema = (id) => {
    return id ? updateBannerAdSchema : createBannerAdSchema;
};

function BannerAdCampaign(props) {
    const { campaignId, handleClose, handleUpdateCampaign } = props;
    const [showModal, setShowModal] = useState(false);
    const dispatch = useDispatch();
    const { company } = useSelector((state) => state.company);
    const { campaign } = useSelector((state) => state.campaign);
    const { message } = useSelector((state) => state.bannerAdCampaign);
    const classes = useStyles();
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors, isValid },
        watch,
    } = useForm({
        resolver: yupResolver(getValidationSchema(campaign?.message?.id)),
        defaultValues: {
            name: '',
            bannerAd: null,
            bannerAdUrl: null,
            isChecked: false,
        },
    });

    const onDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];
            const blobUrl = URL.createObjectURL(file);
            setValue('bannerAdUrl', blobUrl);
            setValue('bannerAd', file);
        },
        [setValue]
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            
        },
    });

    const onFileSelect = (file) => {
        setValue('bannerAdUrl', URL.createObjectURL(file));
        setValue('bannerAd', file);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        onFileSelect(file);
    };

    const onSubmit = (data) => {
        if (data.isChecked) {
            const formData = new FormData();
            formData.append('name', data.name);

            if (data.bannerAd) {
                formData.append('image', data.bannerAd);
            }

            if (campaign.message === null || campaign.message?.id === null) {
                dispatch(
                    createMessage({
                        companyId: company.id,
                        campaignId: campaignId,
                        data: formData,
                    })
                ).then(()=> {
                    dispatch(
                        getCampaignDetails({
                            companyId: company.id,
                            campaignId: campaignId,
                        })
                    );
                }).catch(error => console.error("Error in Creating Message for Banner Ad Campaign", error));
            } else {
                dispatch(
                    updateMessage({
                        companyId: company.id,
                        campaignId: campaignId,
                        data: formData,
                        messageId: campaign.message.id,
                    })
                );
            }
        } else {
            setShowModal(!showModal);
        }
    };

    const handleSave = () => {
        handleSubmit(onSubmit);
    };

    const handleFinalizeClick = () => {
        if (isValid && getValues('isChecked') === true) {
            const formData = new FormData();
            formData.append('name', getValues('name'));

            if (getValues('bannerAd')) {
                formData.append('image', getValues('bannerAd'));
            }
            if (campaign.message === null || campaign.message?.id === null) {
                dispatch(createMessage({
                    companyId: company.id,
                    campaignId: campaignId,
                    data: formData,
                })).then(async()=> {
                    await handleUpdateCampaign();
                    handleClose();
                    dispatch(getAllCampaigns({ companyId: company.id }));
                }).catch(error => console.error("Error in Creating Message for Banner Ad Campaign", error));
            } else {
                dispatch(updateMessage({
                    companyId: company.id,
                    campaignId: campaignId,
                    data: formData,
                    messageId: campaign.message.id,
                })).then(async()=> {
                    await handleUpdateCampaign();
                    handleClose();
                    dispatch(getAllCampaigns({ companyId: company.id }));
                }).catch(error => console.error("Error in Updating Message for Banner Ad Campaign", error));
            }
        } else {
            setShowModal(!showModal);
        }
    };

    useEffect(() => {
        if (campaignId !== campaign.id) {
            dispatch(
                getCampaignDetails({
                    companyId: company.id,
                    campaignId: campaignId,
                })
            );
        }
    }, []);

    useEffect(() => {
        if (campaign && campaign.message) {
            setValue('name', campaign.message.name);
            setValue('bannerAdUrl', campaign.message.image_url);
            
            if(campaign.message.id){
                setValue('resolver', yupResolver(getValidationSchema(campaign.message.id)));
            }
        }
        
    }, [campaign, campaignId, company]);

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Box className="padding-24px pt-0">
                        <Box className="pb-12px">
                            <Typography className="text-lg-bold gray-900">
                                Banner Ad Campaign
                            </Typography>
                        </Box>
                        <Box className="campaign-message-container pb-12px">
                            <Box className="flex-row-center-start gap-6">
                                <Box className="padding-10px">
                                    <ErrorIcon />
                                </Box>
                                <Typography className="gray-900 text-md-regular">
                                    This is a live campaign, please only upload/add
                                    any content that has been approved by the
                                    Medical, Legal and Regulatory reviewers. Unless
                                    final approval from the reviewers is received,
                                    please do not upload any content.
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <form
                                id="bannerAdCampaign"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Box className="pt-12px pb-12px">
                                    <Typography className="text-sm-medium gray-700">
                                        Name
                                    </Typography>
                                    <input
                                        className="custom-text-field"
                                        placeholder="Name"
                                        {...register('name')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.name?.message}
                                    </span>
                                </Box>

                                <Box className="pb-12px text-align-center">
                                    {getValues('bannerAdUrl') && (
                                        <img
                                            src={getValues('bannerAdUrl')}
                                            width="50%"
                                            height="auto"
                                        />
                                    )}
                                </Box>

                                <Box className="pb-12px">
                                    <Box
                                        className="flex-column-center border-1-gray-300 br-16px padding-tb-16-rl-24 wh-100"
                                        {...getRootProps()}
                                    >
                                        <Box className="wh-40px bg-gray-100 br-50 border-6-gray-50">
                                            <Upload />
                                        </Box>
                                        <Box className="flex-row gap-4">
                                            <Typography className="text-sm-medium primary-600">
                                                Click to upload
                                            </Typography>
                                            <Typography className="text-sm-regular gray-500">
                                                or drag and drop
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography className="text-sm-regular gray-500">
                                                PNG, JPG or JPEG
                                            </Typography>
                                        </Box>
                                        <input
                                            {...getInputProps()}
                                            id="bannerAdFileInput"
                                        />

                                        <input
                                            type="file"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                            id="fileInput"
                                        />

                                        <span className="text-xs-regular error-500">
                                            {errors.bannerAd?.message}
                                        </span>
                                    </Box>
                                </Box>

                                <Box className="flex-row-start-center gap-8">
                                    <Checkbox
                                        classes={{
                                            root: classes.root,
                                            checked: classes.checked,
                                        }}
                                        {...register('isChecked')}
                                    />
                                    <Typography className="text-sm-medium gray-700">
                                        I acknowledge that this campaign has met the
                                        required reviewer approvals
                                    </Typography>
                                </Box>

                                <Box className="pt-12px pb-24px">
                                    <CustomButton
                                        title="Save"
                                        titleColor="#FFFFFF"
                                        buttonColor="#AD509E"
                                        handleClick={handleSave}
                                        type="submit"
                                    />
                                </Box>
                            </form>
                        </Box>

                        {campaign.message && campaign.message.id ? (<Box> 
                            <BannerAdEchos />
                        </Box>) : null}

                        <Box className="pt-12px pb-24px">
                            <CustomButton
                                title="Finalize Campaign"
                                titleColor="#FFFFFF"
                                buttonColor="#AD509E"
                                handleClick={handleFinalizeClick}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <ConfirmationModal
                open={showModal}
                onClose={()=> setShowModal(!showModal)}
                content={
                    <Box>
                        <Typography className="mb-20px mt-20px" variant="body1">
                            You Need to Acknowledge this Banner Ad Campaign
                        </Typography>
                    </Box>
                }
                title={
                    <Typography className="queries-table-title">
                        Acknowledge Campaign
                    </Typography>
                }
                primaryButtonText={'Ok'}
                isCancelShow={false}
                onConfirm={()=> setShowModal(!showModal)}
            />
        </>
    );
}

export default BannerAdCampaign;
